import { useState, useEffect, useCallback } from 'react';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  getAdditionalUserInfo as firebaseGetAdditionalUserInfo,
  onIdTokenChanged as firebaseOnIdTokenChanged,
  UserCredential,
  User,
} from 'firebase/auth';
import { log } from '@/logger/logger.ts';
import { auth } from '../../firebase';
import {
  initiateFigmaRedirectSignIn,
  signInWithAuthorizationCode,
  getStoredPromotion,
  clearOidcAccessToken,
  getOidcAccessToken,
} from './oidc-providers/figma-oidc-provider';
import { INTERNAL_AUTH_ERROR_CODES, InternalAuthError } from './firebase-errors';
import type { AuthUser } from '@/types/auth.types';
import type { AuthProviderHook, SignInResult } from './../authProvider.types';

const logger = log.getLogger('useFirebaseAuthProvider');

type UserFromCallback = User | null;

export const useFirebaseAuthProvider = (): AuthProviderHook => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  const updateSessionToken = useCallback(async (firebaseUser) => {
    if (firebaseUser) {
      try {
        const token = await firebaseUser.getIdToken();
        setSessionToken(token);
      } catch (error) {
        logger.error('Error getting ID token:', error);
        setSessionToken(null);
      }
    } else {
      setSessionToken(null);
    }
  }, []);

  // handle user state changes
  useEffect(() => {
    const unsubscribe = firebaseOnAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser as UserFromCallback);
      void updateSessionToken(firebaseUser);
      setIsLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle id token changes
  useEffect(() => {
    if (!user) {
      return;
    }

    const unsubscribe = firebaseOnIdTokenChanged(auth, (firebaseUser) => {
      void updateSessionToken(firebaseUser);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const initiateRedirectSignIn = useCallback(async (provider, redirectPath: string) => {
    if (provider !== 'figma') {
      throw new Error('Unsupported provider');
    }
    await initiateFigmaRedirectSignIn(redirectPath);
  }, []);

  const signIn = useCallback(
    async (
      provider: 'google' | 'microsoft' | 'figma',
      options?: {
        hintEmail?: string;
        microsoftTenantId?: string;
        authorizationCode?: string;
      },
    ) => {
      // Handle Figma OAuth redirect flow
      if (provider === 'figma') {
        if (!options?.authorizationCode) {
          throw new InternalAuthError(
            INTERNAL_AUTH_ERROR_CODES.OIDC_MISSING_CODE,
            'Figma authorization code is required',
          );
        }

        return signInWithAuthorizationCode(options.authorizationCode);
      }

      // Handle Google and Microsoft popup flows
      let firebaseProvider: OAuthProvider | GoogleAuthProvider;
      const customParams: { tenant?: string; login_hint?: string } = {};

      if (provider === 'google') {
        firebaseProvider = new GoogleAuthProvider();
      } else if (provider === 'microsoft') {
        firebaseProvider = new OAuthProvider('microsoft.com');

        if (options?.microsoftTenantId) {
          customParams.tenant = options.microsoftTenantId;
        }
      } else {
        throw new InternalAuthError(INTERNAL_AUTH_ERROR_CODES.UNSUPPORTED_PROVIDER, 'Unsupported provider');
      }

      if (options?.hintEmail) {
        customParams.login_hint = options.hintEmail;
      }
      firebaseProvider.setCustomParameters(customParams);

      const result = await signInWithPopup(auth, firebaseProvider);

      // Return the result directly like the original FirebaseAuthProvider
      return result as SignInResult;
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    const result = await firebaseSignOut(auth);
    clearOidcAccessToken();
    return result;
  }, []);

  const getCurrentUser = useCallback(() => {
    return auth.currentUser;
  }, []);

  const getAdditionalUserInfo = useCallback((result: SignInResult) => {
    return firebaseGetAdditionalUserInfo(result as UserCredential);
  }, []);

  const getIdToken = useCallback(async () => {
    return auth.currentUser?.getIdToken() || null;
  }, []);

  return {
    signIn,
    signOut,
    initiateRedirectSignIn,
    getIdToken,
    getCurrentUser,
    getAdditionalUserInfo,
    getStoredPromotion,
    getOidcAccessToken,
    user,
    isLoading,
    accessToken: sessionToken,
  };
};
